import { useFrame } from "@react-three/fiber";
import React, { useRef, useMemo } from "react";
import * as THREE from "three";
import type { WorldMapConfig } from "../../types/WorldCfg.types";
import { MapStatus } from "../../types/Status.types";
import { useUI } from "../../stores/useUI";
import { ISLAND_OUTLINE_THICKNESS, ISLAND_OUTLINE_OPACITY, OUTLINE_COLOR } from "../../config/game";
import { computeSmoothedNormals } from "../../utils/computeSmoothedNormals";

interface IslandProps {
  map: WorldMapConfig;
  position: [number, number, number];
  status: MapStatus;
  onSelect: () => void;
  gltf?: any;
}

export default function Island({ map, position, status, onSelect, gltf }: IslandProps) {
  const hoveredMapId = useUI((state) => state.hoveredMapId);
  const setHoveredMapId = useUI((state) => state.setHoveredMapId);
  
  // Clone the scene to avoid reusing the same instance
  const baseScene: THREE.Object3D | undefined = gltf?.scene;

  const clonedScene = useMemo(() => {
    if (!baseScene) return undefined;
    const clone = baseScene.clone();

    const outlineMaterial = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      transparent: true,
      uniforms: {
        outlineThickness: { value: ISLAND_OUTLINE_THICKNESS },
        outlineColor: { value: OUTLINE_COLOR },
        outlineOpacity: { value: ISLAND_OUTLINE_OPACITY },
      },
      vertexShader: `
        #include <common>
        #include <logdepthbuf_pars_vertex>
        attribute vec3 smoothNormal;
        uniform float outlineThickness;
        void main() {
          vec4 viewPos = modelViewMatrix * vec4(position, 1.0);
          vec3 viewNormal = normalize(normalMatrix * smoothNormal);
          float dist = -viewPos.z;
          viewPos.xyz += viewNormal * outlineThickness * dist * 0.001;
          gl_Position = projectionMatrix * viewPos;
          #include <logdepthbuf_vertex>
        }
      `,
      fragmentShader: `
        #include <logdepthbuf_pars_fragment>
        uniform vec3 outlineColor;
        uniform float outlineOpacity;
        void main() {
          #include <logdepthbuf_fragment>
          gl_FragColor = vec4(outlineColor, outlineOpacity);
        }
      `,
    });

    const meshesToOutline: { mesh: THREE.Mesh; parent: THREE.Object3D }[] = [];
    clone.traverse((child: any) => {
      if (child.isMesh) {
        child.castShadow = false;
        child.receiveShadow = false;
        if (!child.geometry.getAttribute('smoothNormal')) {
          computeSmoothedNormals(child.geometry);
        }
        meshesToOutline.push({ mesh: child, parent: child.parent! });
      }
    });

    for (const { mesh, parent } of meshesToOutline) {
      const outlineMesh = new THREE.Mesh(mesh.geometry, outlineMaterial);
      outlineMesh.renderOrder = 1;
      outlineMesh.frustumCulled = false;
      outlineMesh.position.copy(mesh.position);
      outlineMesh.rotation.copy(mesh.rotation);
      outlineMesh.scale.copy(mesh.scale);
      parent.add(outlineMesh);
    }

    return clone;
  }, [baseScene]);

  const meshRef = useRef<THREE.Group>(null);
  const hoverProgress = useRef(0);
  const scale = map.model.scale;

  const isHovered = hoveredMapId === map.id;

  useFrame((state, delta) => {
    if (meshRef.current) {
      // Острів коливається лише ВГОРУ від базової позиції (ніколи не йде всередину сфери океану)
      // (sin + 1) / 2 → [0, 1], тому baseY завжди >= position[1]
      const floatT = (Math.sin(state.clock.elapsedTime + position[0]) + 1.0) * 0.5;
      const baseY = position[1] + floatT * 0.06;
      
      // Smooth hover animation
      const targetHover = isHovered ? 1 : 0.1;
      hoverProgress.current += (targetHover - hoverProgress.current) * delta * 4; // Faster animation
      
      // More dramatic lift and effects
      const liftAmount = hoverProgress.current * 0.4; // Increased from 0.15
      const rotationAmount = hoverProgress.current * 0.1; // Slightly increased
      const scaleAmount = 1 + (hoverProgress.current * 0.1); // Scale up on hover
      const scaleTextAmount = 1 + hoverProgress.current * 0.5; // Scale up on hover
      
      // Gentle floating animation with smooth hover
      meshRef.current.position.y = baseY + liftAmount;

      // Scale effect
      meshRef.current.scale.setScalar(scale * scaleAmount);

      // Hover effects
      if (hoverProgress.current > 0.01) {
        meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 2) * rotationAmount;
      } else {
        meshRef.current.rotation.y = 0;
      }

    }
  });

  return (
    <group position={position}>
      {clonedScene && (
        <primitive
          object={clonedScene}
          ref={meshRef}
          onClick={() => { document.body.style.cursor = 'auto'; onSelect(); }}
          onPointerOver={() => {
            setHoveredMapId(map.id);
            document.body.style.cursor = status === MapStatus.LOCKED ? 'default' : 'pointer';
          }}
          // onPointerOut={() => {
          //   setHoveredMapId(null);
          //   document.body.style.cursor = 'auto';
          // }}
          scale={scale}
        />
      )}
    </group>
  );
}